/* ==========================================
   TA Predictive Search v2.0
   Torre Arka
========================================== */

(() => {
  if (window.TASearchLoaded) return;
  window.TASearchLoaded = true;

  const search = document.querySelector('[data-ta-search]');

  if (!search) return;

  const input = search.querySelector('[data-ta-search-input]');
  const clearButton = search.querySelector('[data-ta-search-clear]');
  const form = search.querySelector('[data-ta-search-form]');
  const initial = search.querySelector('[data-ta-search-initial]');
  const loading = search.querySelector('[data-ta-search-loading]');
  const results = search.querySelector('[data-ta-search-results]');
  const noResults = search.querySelector('[data-ta-search-no-results]');
  const closeButtons = search.querySelectorAll('[data-ta-search-close]');
  const chips = search.querySelectorAll('[data-ta-search-term]');

  let debounceTimer;
  let requestController;
  let activeResultIndex = -1;

  function localizedRoute(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${path}`.replace(/\/{2,}/g, '/');
  }

  function openSearch() {
    search.classList.add('is-open');
    search.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ta-search-open');

    window.setTimeout(() => {
      input.focus();
    }, 220);
  }

  function closeSearch() {
    search.classList.remove('is-open');
    search.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ta-search-open');
    activeResultIndex = -1;
  }

  function setState(state) {
    initial.hidden = state !== 'initial';
    loading.hidden = state !== 'loading';
    results.hidden = state !== 'results';
    noResults.hidden = state !== 'empty';

    input.setAttribute(
      'aria-expanded',
      state === 'results' ? 'true' : 'false'
    );
  }

  function resetSearch() {
    window.clearTimeout(debounceTimer);

    if (requestController) {
      requestController.abort();
    }

    results.innerHTML = '';
    activeResultIndex = -1;
    setState('initial');
  }

  function resultLinks() {
    return Array.from(
      results.querySelectorAll('[data-ta-result-link]')
    );
  }

  function setActiveResult(index) {
    const links = resultLinks();

    if (!links.length) return;

    links.forEach((link) => {
      link.classList.remove('is-keyboard-active');
      link.removeAttribute('aria-selected');
    });

    if (index < 0) {
      activeResultIndex = -1;
      return;
    }

    if (index >= links.length) index = 0;

    activeResultIndex = index;

    const activeLink = links[activeResultIndex];
    activeLink.classList.add('is-keyboard-active');
    activeLink.setAttribute('aria-selected', 'true');

    activeLink.scrollIntoView({
      block: 'nearest'
    });
  }

  async function predictiveSearch(query) {
    if (requestController) {
      requestController.abort();
    }

    requestController = new AbortController();

    setState('loading');

    const parameters = new URLSearchParams({
      q: query,
      'resources[type]': 'product,collection,page,article',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]':
        'title,product_type,variants.title,vendor,tag',
      section_id: 'ta-search'
    });

    try {
      const response = await fetch(
        `${localizedRoute('search/suggest')}?${parameters.toString()}`,
        {
          signal: requestController.signal,
          headers: {
            Accept: 'text/html'
          }
        }
      );

      if (!response.ok) {
        throw new Error('No se pudo completar la búsqueda.');
      }

      const html = await response.text();
      const documentResult = new DOMParser().parseFromString(
        html,
        'text/html'
      );

      const source = documentResult.querySelector(
        '[data-ta-predictive-results-source]'
      );

      if (!source) {
        throw new Error('Shopify no devolvió resultados predictivos.');
      }

      const isEmpty = Boolean(
        source.querySelector('[data-ta-predictive-empty]')
      );

      if (isEmpty) {
        results.innerHTML = '';
        setState('empty');
        return;
      }

      results.innerHTML = source.innerHTML;
      activeResultIndex = -1;
      setState('results');
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error('TA Search:', error);
      results.innerHTML = '';
      setState('empty');
    }
  }

  function queueSearch() {
    const query = input.value.trim();

    clearButton.hidden = query.length === 0;

    window.clearTimeout(debounceTimer);

    if (query.length < 2) {
      resetSearch();
      return;
    }

    debounceTimer = window.setTimeout(() => {
      predictiveSearch(query);
    }, 260);
  }

  /*
   * Capturamos el clic antes de que Dawn abra
   * su modal de búsqueda nativo.
   */
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest(
        '.header__icon--search, a[href*="/search"]'
      );

      if (!trigger || search.contains(trigger)) return;

      event.preventDefault();
      event.stopPropagation();
      openSearch();
    },
    true
  );

  closeButtons.forEach((button) => {
    button.addEventListener('click', closeSearch);
  });

  chips.forEach((chip) => {
    chip.addEventListener('click', () => {
      input.value = chip.dataset.taSearchTerm || '';
      clearButton.hidden = false;
      input.focus();
      queueSearch();
    });
  });

  input.addEventListener('input', queueSearch);

  clearButton.addEventListener('click', () => {
    input.value = '';
    clearButton.hidden = true;
    resetSearch();
    input.focus();
  });

  input.addEventListener('keydown', (event) => {
    const links = resultLinks();

    if (event.key === 'ArrowDown' && links.length) {
      event.preventDefault();
      setActiveResult(activeResultIndex + 1);
    }

    if (event.key === 'ArrowUp' && links.length) {
      event.preventDefault();

      if (activeResultIndex <= 0) {
        setActiveResult(links.length - 1);
      } else {
        setActiveResult(activeResultIndex - 1);
      }
    }

    if (
      event.key === 'Enter' &&
      activeResultIndex >= 0 &&
      links[activeResultIndex]
    ) {
      event.preventDefault();
      window.location.href = links[activeResultIndex].href;
    }
  });

  form.addEventListener('submit', (event) => {
    if (!input.value.trim()) {
      event.preventDefault();
      input.focus();
    }
  });

  results.addEventListener('click', (event) => {
    if (event.target.closest('[data-ta-result-link]')) {
      closeSearch();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeSearch();
    }
  });
})();